/*
 * Homepage "New arrivals" strip.
 *
 * The automated New Arrivals collection lags behind by up to ~80 minutes, so
 * the tiles Shopify renders into the HTML can be stale and can differ between
 * requests. We replace them with /collections/all sorted newest-first, which
 * has no propagation lag.
 *
 * Two things keep the shopper from ever watching one list turn into another:
 *
 *  - The strip is held invisible until the swap lands.
 *  - The winning markup is cached in sessionStorage, so every later page view
 *    in the same tab swaps synchronously, before the browser paints. Only the
 *    first view in a session waits on the network at all.
 *
 * A timeout guarantees the strip is revealed even if the fetch hangs, and any
 * failure leaves the server-rendered tiles in place.
 */
(function () {
  'use strict';

  var SRC = '/collections/all?sort_by=created-descending&section_id=main-collection-product-grid';
  var HREF = '/collections/all?sort_by=created-descending';
  var KEY = 'cd-latest-v1';
  var MAX_AGE = 10 * 60 * 1000; // Ten minutes: long enough for a browsing session.
  var GUARD_MS = 6000; // Generous — a slow connection must not expose the stale list.

  // Dawn's reveal observer is only wired to elements present at load, so cards
  // added later would stay stuck at opacity 0 unless we strip these classes.
  function clean(li, asSlide) {
    li.classList.remove('scroll-trigger', 'animate--slide-in');
    li.removeAttribute('data-cascade');
    li.style.removeProperty('--animation-order');
    li.querySelectorAll('.scroll-trigger').forEach(function (el) {
      el.classList.remove('scroll-trigger', 'animate--slide-in');
      el.removeAttribute('data-cascade');
    });
    if (asSlide) li.classList.add('slider__slide');
    return li;
  }

  function readCache() {
    try {
      var raw = sessionStorage.getItem(KEY);
      if (!raw) return null;
      var box = JSON.parse(raw);
      if (!box || !box.html || Date.now() - box.t > MAX_AGE) return null;
      return box.html;
    } catch (e) {
      return null; // Private mode, quota, corrupt entry — all mean "no cache".
    }
  }

  function writeCache(html) {
    try {
      sessionStorage.setItem(KEY, JSON.stringify({ t: Date.now(), html: html }));
    } catch (e) {
      /* Not being able to cache is not a failure. */
    }
  }

  // Turns the fetched collection page into the tiles this strip needs.
  function extract(html, want, asSlide) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var items = [].slice.call(doc.querySelectorAll('#product-grid > li'), 0, want);
    if (!items.length) return '';
    return items
      .map(function (li) {
        return clean(li, asSlide).outerHTML;
      })
      .join('');
  }

  function paint(grid, html) {
    if (!html) return false;
    grid.innerHTML = html;
    return true;
  }

  function run() {
    var sec = document.querySelector('[id$="__new_arrivals"]');
    if (!sec || sec.dataset.cdLatest) return;
    var grid = sec.querySelector('.grid');
    if (!grid || !grid.children.length) return;
    sec.dataset.cdLatest = 'on';

    // "View all" would otherwise land on the lagging collection.
    sec.querySelectorAll('a[href*="/collections/new-arrivals"]').forEach(function (a) {
      a.setAttribute('href', HREF);
    });

    var want = grid.children.length;
    var asSlide = grid.firstElementChild.classList.contains('slider__slide');

    function repage() {
      var sc = sec.querySelector('slider-component');
      if (sc && typeof sc.initPages === 'function') {
        try {
          sc.initPages();
        } catch (e) {
          /* non-fatal */
        }
      }
    }

    // Fast path: this tab has already seen the right answer. Swap before the
    // browser paints, so a refresh shows the correct list with no flicker at all.
    var cached = readCache();
    var painted = false;
    if (cached && paint(grid, cached)) {
      painted = true;
      repage();
    }

    var revealed = painted;
    if (!painted) grid.style.visibility = 'hidden';
    function reveal() {
      if (revealed) return;
      revealed = true;
      grid.style.visibility = '';
    }
    var guard = setTimeout(reveal, GUARD_MS);

    fetch(SRC)
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var tiles = extract(html, want, asSlide);
        if (!tiles) return;
        writeCache(tiles);
        // If the cache already painted this exact markup, changing the DOM
        // again would be the very flicker we are trying to remove.
        if (painted && tiles === cached) return;
        if (paint(grid, tiles)) repage();
      })
      .catch(function () {
        /* Leave whatever is on screen — cached tiles, or the server's. */
      })
      .then(function () {
        clearTimeout(guard);
        reveal();
      });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  document.addEventListener('shopify:section:load', run);
})();
